import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { Loader2, Camera, Check } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../../contexts/LanguageContext';
import { messagesApi, connectionsApi } from '../../services/messagesApi';
import { postsApi } from '../../services/postsApi';

const NewGroupDialog = ({ open, onClose, language, onCreated }) => {
  const { t } = useLanguage();
  const [name, setName] = useState('');
  const [people, setPeople] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const photoRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setName(''); setSelected(new Set()); setLoading(true); setPhoto(null); setPhotoPreview(null);
    (async () => {
      try {
        const [f, fo] = await Promise.all([connectionsApi.friends(), connectionsApi.following()]);
        const map = {};
        [...f, ...fo].forEach((u) => { map[u.user_id] = u; });
        setPeople(Object.values(map));
      } catch { setPeople([]); }
      finally { setLoading(false); }
    })();
  }, [open]);

  const pickPhoto = (e) => { const f = e.target.files?.[0]; if (f) { setPhoto(f); setPhotoPreview(URL.createObjectURL(f)); } };
  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const create = async () => {
    if (selected.size === 0) { toast.error(t('messages.pickAtLeastOne')); return; }
    setBusy(true);
    try {
      let imageUrl = null;
      if (photo) { const up = await postsApi.uploadImage(photo, photo.name); imageUrl = up.image_url; }
      const res = await messagesApi.createGroup(name || t('messages.groupFallback'), Array.from(selected), imageUrl);
      toast.success(t('messages.groupCreated'));
      onCreated?.(res.thread);
    } catch (e) { toast.error(e?.response?.data?.detail || t('messages.error')); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent data-testid="new-group-dialog">
        <DialogHeader>
          <DialogTitle>{t('messages.newGroup')}</DialogTitle>
          <DialogDescription>{t('messages.newGroupDesc')}</DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-3">
          <button onClick={() => photoRef.current?.click()} className="w-14 h-14 rounded-full bg-muted flex items-center justify-center overflow-hidden shrink-0" data-testid="new-group-photo-btn">
            {photoPreview ? <img src={photoPreview} alt="" className="w-full h-full object-cover" /> : <Camera className="w-5 h-5 text-muted-foreground" />}
          </button>
          <input ref={photoRef} type="file" accept="image/*" className="hidden" onChange={pickPhoto} data-testid="new-group-photo-input" />
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t('messages.groupName')} data-testid="new-group-name" />
        </div>
        <div className="max-h-60 overflow-y-auto space-y-1 mt-2">
          {loading ? <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
            : people.length === 0 ? <p className="text-sm text-muted-foreground text-center py-6">{t('messages.addFriendsFirst')}</p>
            : people.map((u) => (
              <button key={u.user_id} onClick={() => toggle(u.user_id)} className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-muted text-left" data-testid={`new-group-member-${u.user_id}`}>
                <Avatar className="w-8 h-8"><AvatarFallback className="bg-primary/15 text-xs">{(u.name || '?').slice(0, 2).toUpperCase()}</AvatarFallback></Avatar>
                <span className="text-sm flex-1">{u.name}</span>
                <span className={`w-5 h-5 rounded-full border flex items-center justify-center ${selected.has(u.user_id) ? 'bg-primary border-primary text-primary-foreground' : 'border-muted-foreground/40'}`}>
                  {selected.has(u.user_id) && <Check className="w-3 h-3" />}
                </span>
              </button>
            ))}
        </div>
        <DialogFooter>
          <Button onClick={create} disabled={busy} className="w-full" data-testid="new-group-create">{busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}{t('messages.createGroup')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default NewGroupDialog;
